import { Sidebar } from "@/components/Sidebar";
import { BoardView } from "@/components/BoardView";
import { TodoView } from "@/components/TodoView";
import { NotesView } from "@/components/NotesView";
import { ChatPanel } from "@/components/ChatPanel";

import { useLocation } from "react-router-dom";
import { DrawingRoomProvider } from "@/contexts/DrawingRoomContext";
import { RoomSettingsProvider } from "@/contexts/RoomSettingsContext";
import { PermissionsProvider } from "@/contexts/PermissionsContext";
import { useToast } from "@/components/ui/use-toast";

export type ViewType = 'board' | 'todo' | 'notes'; // Removed 'access-control'

import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

const App = () => {
  const [activeView, setActiveView] = useState<ViewType>('board');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Mobile sidebar state
  const [isChatOpen, setIsChatOpen] = useState(false); // Mobile chat drawer state
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const roomId: string = searchParams.get('room') || '';
  const displayName = searchParams.get('user') || '';
  const [blocked, setBlocked] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [participantsCount, setParticipantsCount] = useState(0);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    let subscription: any = null;
    async function checkAccess() {
      // 1. Check for either a one-time entry ticket OR a persistent session token.
      const ticketString = sessionStorage.getItem(`room_access_token_${roomId}`);
      const sessionToken = localStorage.getItem(`session_token_${roomId}`);

      // If the user has neither a valid entry ticket nor a persistent session, deny access.
      if (!ticketString && !sessionToken) {
        setBlocked('Access denied. Please join or create rooms from the home page.');
        return;
      }

      // 2. If an entry ticket exists, it must be valid. This is for first-time entry.
      if (ticketString) {
        try {
          const ticket = JSON.parse(ticketString);
          if (!ticket || !ticket.token || ticket.username !== displayName) {
            throw new Error('Invalid or tampered ticket');
          }
          // The ticket is valid, so consume it. The user will rely on the sessionToken for reloads.
          sessionStorage.removeItem(`room_access_token_${roomId}`);
          console.log('Access ticket validated successfully for user:', ticket.username);
        } catch (error) {
          setBlocked('Access denied. Invalid or tampered access ticket.');
          return;
        }
      }

      // Debug: log displayName and roomId
      console.log('Joining room as displayName:', displayName, 'in room:', roomId);
      // Block if missing room or user
      if (!roomId || !displayName) {
        setBlocked('Missing room or user information in URL.');
        return;
      }

      // 1. Get or create session token for this room
      let session_token = localStorage.getItem(`session_token_${roomId}`);
      if (!session_token) {
        session_token = window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now();
        localStorage.setItem(`session_token_${roomId}`, session_token);
      }
      setSessionToken(session_token);

      // 2. Upsert participant (allow duplicate display names)
      await supabase.from('room_participants').upsert({
        room_id: roomId,
        participant_name: displayName,
        session_token,
        joined_at: new Date().toISOString(),
      });

      // 3. Block if user is banned (by session_token)
      const { data: kicked } = await supabase
        .from('room_kicked')
        .select('id')
        .eq('room_id', roomId)
        .eq('session_token', session_token)
        .maybeSingle();
      if (kicked) {
        setBlocked('You have been removed from this room and cannot access it.');
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
        return;
      }
      setBlocked(null);

      // 4. Fetch room data to get creator session token
      const { data: roomData, error: roomError } = await supabase
        .from('rooms')
        .select('creator_session_token')
        .eq('id', roomId)
        .single<{ creator_session_token: string }>();

      if (roomError) {
        console.warn('Error fetching room data:', roomError);
        return;
      }

      if (roomData) {
        setIsAdmin(roomData.creator_session_token === session_token);
      } else {
        console.warn('Could not fetch room data');
      }
      
      // Define types for the ban payload
      interface RoomKickedRow {
        id: string;
        room_id: string;
        participant_name: string;
        kicked_by?: string;
        kicked_at: string;
      }
      
      type BanPayload = {
        eventType: 'INSERT' | 'UPDATE' | 'DELETE';
        new: RoomKickedRow;
        old?: {
          id: string;
          participant_name: string;
        };
      }

      console.log('Subscribing to room_kicked for room:', roomId, 'as user:', displayName);
      
      // Subscribe to all bans in this room, check in callback
      subscription = supabase
        .channel(`room_kicked_${roomId}_${displayName}`) // Unique channel per user and room
        .on('postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'room_kicked',
            filter: `room_id=eq.${roomId}`
          },
          (payload) => {
            const banPayload = payload as unknown as BanPayload;
            console.log('room_kicked event payload:', payload);
            
            // Handle different event types
            if (banPayload.eventType === 'INSERT' || banPayload.eventType === 'UPDATE') {
              const banData = banPayload.new;
              const bannedUser = banData.participant_name;
              
              if (bannedUser.toLowerCase() === displayName.toLowerCase()) {
                console.log('Current user was banned!', banData);
                const bannedBy = 'kicked_by' in banData ? banData.kicked_by : undefined;
                const banMessage = bannedBy 
                  ? `You have been banned by ${bannedBy} from this room.`
                  : 'You have been removed from this room.';
                
                setBlocked(banMessage);
                
                // Force a page refresh to ensure clean state
                setTimeout(() => {
                  window.location.href = '/';
                }, 3000);
              }
            }
          }
        )
        .subscribe((status, err) => {
          if (err) {
            console.error('Subscription error:', err);
          } else {
            console.log('Subscription status:', status);
          }
        });
    }
    checkAccess();
    return () => {
      if (subscription) supabase.removeChannel(subscription);
    };
  }, [roomId, displayName]);

  // Add real-time subscription for room deletion
  useEffect(() => {
    if (!roomId) return;

    // Subscribe to room deletion events
    const roomSubscription = supabase
      .channel(`room_${roomId}_deletion`)
      .on('postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'rooms',
          filter: `id=eq.${roomId}`
        },
        (payload) => {
          console.log('Room deleted:', payload);
          toast({
            title: 'Room Deleted',
            description: 'This room has been deleted by the creator.',
            variant: 'destructive',
          });
          
          // Clear the session token
          localStorage.removeItem(`session_token_${roomId}`);
          
          // Redirect to home page after a short delay
          setTimeout(() => {
            window.location.href = '/';
          }, 2000);
        }
      )
      .subscribe((status, err) => {
        if (err) {
          console.error('Room deletion subscription error:', err);
        }
      });

    return () => {
      roomSubscription.unsubscribe();
    };
  }, [roomId]);

  if (blocked) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="p-8 bg-white rounded-xl shadow text-center max-w-sm">
          <h2 className="text-lg font-bold mb-2 text-red-600">Access Denied</h2>
          <p className="text-gray-700 mb-4">{blocked}</p>
          <a href="/" className="text-blue-600 underline">Return to Home</a>
        </div>
      </div>
    );
  }

  return (
    <RoomSettingsProvider roomId={roomId}>
      <DrawingRoomProvider>
        <div className="h-screen bg-white flex flex-col overflow-hidden relative" style={{ isolation: 'isolate' }}>
          {/* Mobile Header with Left Sidebar and Right Chat Hamburger */}
          <div className="md:hidden flex items-center justify-between p-2 border-b border-gray-100 bg-white/90 z-30">
            <button
              className="p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Open sidebar"
              onClick={() => {
                setIsSidebarOpen(true);
                setIsChatOpen(false); // Close chat if open
              }}
            >
              {/* Left Hamburger icon */}
              <svg className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <span className="font-bold text-lg text-gray-700 flex-1 text-center ml-2 mr-2">Made For Teams</span>
            <button
              className="p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Open chat"
              onClick={() => {
                setIsChatOpen(true);
                setIsSidebarOpen(false); // Close sidebar if open
              }}
            >
              {/* Chat (hamburger/message) icon */}
              <svg className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.77 9.77 0 01-4.39-1.012L3 21l1.012-3.39A8.96 8.96 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </button>
          </div>

          {/* Sidebar Overlay (Mobile) */}
          {isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-40 z-40 md:hidden"
              onClick={() => setIsSidebarOpen(false)}
              aria-label="Close sidebar overlay"
            />
          )}

          {/* Sidebar Drawer (Mobile Only) */}
          <div
            className={`fixed top-0 left-0 h-full w-72 bg-white shadow-2xl z-40 transform transition-transform duration-300 ease-in-out md:hidden flex flex-col ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
            style={{ willChange: 'transform', boxShadow: '0 8px 32px 0 rgba(60,60,100,0.25)' }}
          >
            <div className="w-full flex justify-end items-center h-14 px-2 border-b border-gray-100">
              <button
                className="p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Close sidebar"
                onClick={() => setIsSidebarOpen(false)}
              >
                <svg className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <Sidebar
              activeView={activeView}
              onViewChange={(view) => {
                setActiveView(view);
                setIsSidebarOpen(false); // Close sidebar on mobile after navigation
              }}
              onParticipantsChange={setParticipantsCount}
            />
          </div>

          {/* Main Layout */}
          <div className="flex flex-1 overflow-hidden relative">
            {/* Desktop Sidebar (unchanged for md and up) */}
            <div className="border-r border-gray-300 w-72 hidden md:block">
              <Sidebar
                activeView={activeView}
                onViewChange={setActiveView}
                onParticipantsChange={setParticipantsCount}
              />
            </div>
            {/* Main Content Area */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Main Content */}
              <div className="flex-1 flex overflow-hidden">
                {/* Center Workspace */}
                <div className="flex-1 overflow-hidden">
                  <PermissionsProvider roomId={roomId} userName={displayName || 'anonymous'}>
                    <div className="relative h-full w-full bg-white overflow-hidden border border-gray-300">
                      <BoardView roomId={roomId} isActive={activeView === 'board'} />
                      <div className={`absolute inset-0 ${activeView === 'todo' ? 'block' : 'hidden'}`}>
                        <TodoView roomId={roomId} />
                      </div>
                      <div className={`absolute inset-0 ${activeView === 'notes' ? 'block' : 'hidden'}`}>
                        <NotesView roomId={roomId} />
                      </div>
                    </div>
                  </PermissionsProvider>
                </div>
                {/* Right Chat Panel (desktop only) */}
                <div className="w-88 bg-white border-l border-gray-300 flex-shrink-0 overflow-y-auto hidden md:block">
                  <ChatPanel participantsCount={participantsCount} />
                </div>

                {/* Mobile Chat FAB and Drawer (FAB removed, only Drawer remains) */}
                {isChatOpen && (
                  <>
                    <div
                      className="fixed inset-0 bg-black bg-opacity-30 z-40 md:hidden"
                      onClick={() => setIsChatOpen(false)}
                      aria-label="Close chat overlay"
                    />
                    <div
                      className={`fixed top-0 right-0 h-full w-72 max-w-full bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out md:hidden ${isChatOpen ? 'translate-x-0' : 'translate-x-full'} overflow-y-auto min-h-0`}
                      style={{ willChange: 'transform', boxShadow: '0 8px 32px 0 rgba(60,60,100,0.25)' }}
                    >
                      <div className="flex justify-end p-2">
                        <button
                          className="p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          aria-label="Close chat"
                          onClick={() => setIsChatOpen(false)}
                        >
                          <svg className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <ChatPanel participantsCount={participantsCount || 0} />
                    </div>
                  </>
                )}

              </div>
            </div>
          </div>
        </div>
      </DrawingRoomProvider>
    </RoomSettingsProvider>
  );
};

export default App;
